// Recover from stale chunk references after a deploy.
//
// A client still running a previous build fails to lazily import a route chunk
// whose hashed filename was replaced by the latest deploy, surfacing as an
// unhandled "Failed to fetch dynamically imported module" rejection. Reloading
// the page makes the browser fetch the current index.html and the fresh asset
// names, so the navigation succeeds on the second attempt.

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
]

const reloadMarkerKey = (fullPath: string) => `chunk-reload:${fullPath}`

function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

/**
 * Decide whether a failed navigation should be recovered by reloading the page.
 *
 * Returns `true` only for dynamic-import failures, and at most once per
 * destination: a sessionStorage marker prevents an infinite reload loop when
 * the chunk is genuinely unavailable (e.g. a broken deploy). Clear the marker
 * with {@link clearChunkReloadMarker} once the destination loads successfully.
 */
export function shouldReloadForDynamicImportError(error: unknown, fullPath: string): boolean {
  if (!isDynamicImportError(error)) {
    return false
  }
  const key = reloadMarkerKey(fullPath)
  if (sessionStorage.getItem(key)) {
    return false
  }
  sessionStorage.setItem(key, '1')
  return true
}

/** Clear the reload marker after a destination has loaded successfully. */
export function clearChunkReloadMarker(fullPath: string): void {
  sessionStorage.removeItem(reloadMarkerKey(fullPath))
}
